import { useState, useEffect } from "react";
import { HiX } from "react-icons/hi";
import { CustomLabel, CustomInput, CustomSelect, CustomButton } from "../../../components/FormFields";
import { fetchActiveEmployees } from "../../../services/employeeService";
import { fetchLoanTypes } from "../../../services/loanTypeService";
import EmployeePicker from "../../../components/EmployeePicker";

const iso = (d) => (d ? String(d).slice(0, 10) : "");

/* `loan` set means we are editing one that is already running. Two things change
 * in that mode: the employee is fixed (moving a loan to someone else is a new
 * loan, not an edit), and the installment and outstanding balance become
 * editable. Nadee asked for that second part directly - when a member of staff
 * resigns she has to recover the whole remaining balance in their final month,
 * which the principal-over-installments arithmetic will never produce. */
export default function EmployeeLoanForm({ closeForm, onSave, loan = null, saving = false }) {
  const editing = Boolean(loan);
  const [employees, setEmployees]   = useState([]);
  const [loanTypes, setLoanTypes]   = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [form, setForm] = useState({
    emp_id:             loan?.emp_id ?? "",
    loanTypeId:         loan?.loanTypeId ?? "",
    principal:          loan?.principal ?? "",
    installments:       loan?.installments ?? 12,
    startDate:          iso(loan?.startDate),
    monthlyInstallment: loan?.monthlyInstallment ?? "",
    remaining:          loan?.remaining ?? "",
  });
  // an amount the user typed wins over the calculated one until they change the
  // principal, the count or the type, which is when the calculation is trusted again
  const [manual, setManual] = useState(editing);

  useEffect(() => {
    fetchActiveEmployees().then((d)  => setEmployees(Array.isArray(d) ? d : [])).catch(console.error);
    fetchLoanTypes().then((d) => setLoanTypes(Array.isArray(d) ? d : [])).catch(console.error);
  }, []);

  // the type carries the interest rate, so it has to be resolved once the list arrives
  useEffect(() => {
    if (!loanTypes.length) return;
    setSelectedType(loanTypes.find((lt) => lt.id === +form.loanTypeId) ?? null);
  }, [loanTypes, form.loanTypeId]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const setRecalc = (f) => (e) => { setManual(false); setForm((p) => ({ ...p, [f]: e.target.value })); };

  const handleTypeChange = (e) => {
    setManual(false);
    setSelectedType(loanTypes.find((lt) => lt.id === +e.target.value) ?? null);
    setForm((p) => ({ ...p, loanTypeId: e.target.value }));
  };

  const interest     = selectedType ? Number(selectedType.interestRate) : 0;
  const totalPayable = form.principal ? Math.round(+form.principal * (1 + interest / 100)) : 0;
  // rounded down; the last installment picks up the rupees left over (see installmentFor)
  const calcMonthly  = totalPayable && form.installments ? Math.floor(totalPayable / form.installments) : 0;

  const monthly   = manual && form.monthlyInstallment !== "" ? Number(form.monthlyInstallment) : calcMonthly;
  const remaining = editing
    ? (manual && form.remaining !== "" ? Number(form.remaining) : totalPayable)
    : totalPayable;
  const lastInstallment = form.installments > 1
    ? Math.max(0, totalPayable - monthly * (form.installments - 1))
    : monthly;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.emp_id || saving) return;   // the picker is not a native input, so guard it here
    onSave({
      emp_id:             form.emp_id,
      loanTypeId:         form.loanTypeId,
      loanType:           selectedType?.name ?? loan?.loanType ?? "",
      principal:          form.principal,
      interestRate:       interest,
      totalPayable,
      installments:       form.installments,
      monthlyInstallment: monthly,
      remaining,
      startDate:          form.startDate,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl bg-purple-600 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">{editing ? "Edit Loan" : "Grant Loan"}</h3>
          <button type="button" onClick={closeForm}
            className="rounded-lg p-1.5 text-purple-200 hover:bg-purple-500 hover:text-white">
            <HiX className="h-5 w-5" />
          </button>
        </div>
        <form className="flex flex-col gap-4 px-6 py-6" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-1.5">
            <CustomLabel htmlFor="employee">Employee</CustomLabel>
            <EmployeePicker id="employee" employees={employees} disabled={editing}
              value={form.emp_id} onChange={set("emp_id")} />
            {editing && (
              <p className="text-xs text-gray-400">
                The employee cannot be changed. Close this loan and grant a new one instead.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <CustomLabel htmlFor="loanType">Loan Type</CustomLabel>
            <CustomSelect id="loanType" value={form.loanTypeId} onChange={handleTypeChange} required>
              <option value="">Select Loan Type</option>
              {loanTypes.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
            </CustomSelect>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <CustomLabel htmlFor="principal">Principal Amount (Rs.)</CustomLabel>
              <CustomInput id="principal" type="number" value={form.principal}
                onChange={setRecalc("principal")} placeholder="e.g. 100000" required />
            </div>
            <div className="flex flex-col gap-1.5">
              <CustomLabel htmlFor="installments">No. of Installments</CustomLabel>
              <CustomInput id="installments" type="number" value={form.installments}
                onChange={setRecalc("installments")} required />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <CustomLabel htmlFor="startDate">Start Date</CustomLabel>
            <CustomInput id="startDate" type="date" value={form.startDate} onChange={set("startDate")} required />
          </div>

          {editing && (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
              <p className="col-span-2 text-xs text-amber-800 dark:text-amber-200">
                Adjust these when someone resigns and the balance has to come off their final
                salary. Leave them alone and they follow the principal.
              </p>
              <div className="flex flex-col gap-1.5">
                <CustomLabel htmlFor="monthlyInstallment">Monthly Installment (Rs.)</CustomLabel>
                <CustomInput id="monthlyInstallment" type="number" value={form.monthlyInstallment}
                  onChange={(e) => { setManual(true); set("monthlyInstallment")(e); }} />
              </div>
              <div className="flex flex-col gap-1.5">
                <CustomLabel htmlFor="remaining">Outstanding (Rs.)</CustomLabel>
                <CustomInput id="remaining" type="number" value={form.remaining}
                  onChange={(e) => { setManual(true); set("remaining")(e); }} />
              </div>
            </div>
          )}

          {form.principal > 0 && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-700 dark:bg-purple-900/20">
              <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-purple-500">Loan Summary</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span className="text-gray-500">Interest Rate:</span>   <span className="font-semibold">{interest}%</span>
                <span className="text-gray-500">Total Payable:</span>   <span className="font-semibold text-purple-700">Rs. {totalPayable.toLocaleString()}</span>
                <span className="text-gray-500">Monthly Installment:</span> <span className="font-semibold text-green-700">Rs. {monthly.toLocaleString()}</span>
                {!manual && lastInstallment !== monthly && (<>
                  <span className="text-gray-500">Last Installment:</span>
                  <span className="font-semibold text-green-700">Rs. {lastInstallment.toLocaleString()}</span>
                </>)}
                {editing && (<>
                  <span className="text-gray-500">Outstanding:</span>
                  <span className="font-semibold">Rs. {remaining.toLocaleString()}</span>
                </>)}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-2 dark:border-gray-700">
            <CustomButton color="gray" type="button" onClick={closeForm}>Cancel</CustomButton>
            <CustomButton color="purple" type="submit" disabled={!form.emp_id || saving}>
              {saving ? "Saving…" : editing ? "Save Changes" : "Grant Loan"}
            </CustomButton>
          </div>
        </form>
      </div>
    </div>
  );
}
